import { useEffect, useRef, useCallback } from 'react';

/**
 * Activity events that reset the idle timer.
 * `passive: true` is used so we don't block scroll/touch performance.
 */
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
];

/**
 * Cross-tab sync key. When any tab records activity, every tab
 * resets its timer (so the user doesn't get logged out while
 * actively working in another tab).
 */
const STORAGE_KEY = 'myassets_last_activity_ts';

interface UseIdleTimeoutOptions {
  /** Called once after `timeoutMs` of no user activity. */
  onIdle: () => void;
  /** Idle threshold in milliseconds. Default = 2 hours. */
  timeoutMs?: number;
  /** When false, the hook is a no-op (e.g. user not authenticated). */
  enabled?: boolean;
}

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) across the
 * window AND across browser tabs (via `storage` event). Calls `onIdle`
 * once after the configured period of inactivity.
 */
export const useIdleTimeout = ({
  onIdle,
  timeoutMs = 2 * 60 * 60 * 1000, // 2 hours
  enabled = true,
}: UseIdleTimeoutOptions) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);
  const firedRef = useRef(false);

  // Always invoke the latest onIdle without restarting effects
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      onIdleRef.current();
    }, timeoutMs);
  }, [timeoutMs, clearTimer]);

  const recordActivity = useCallback(
    (broadcast: boolean) => {
      if (firedRef.current) return; // already idle - don't reset
      if (broadcast) {
        try {
          localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch {
          /* storage may be disabled - silently ignore */
        }
      }
      startTimer();
    },
    [startTimer],
  );

  useEffect(() => {
    if (!enabled) {
      firedRef.current = false;
      clearTimer();
      return;
    }

    const handleLocalActivity = () => recordActivity(true);
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) recordActivity(false);
    };

    // Initial timer
    startTimer();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleLocalActivity, { passive: true }),
    );
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleLocalActivity),
      );
      window.removeEventListener('storage', handleStorage);
    };
  }, [enabled, startTimer, clearTimer, recordActivity]);
};

export default useIdleTimeout;
